import { Injectable } from '@angular/core'
import { BehaviorSubject, Subject } from 'rxjs'

/** Auth events shared between browser tabs through BroadcastChannel. */
export type AuthBroadcast = { type: 'logout' } | { type: 'login'; userId: number | string }

/**
 * Holds client-side session state (current user, auth state version, logged-in
 * flag) that used to live inline inside the old god-service `ApiService`.
 * Kept as BehaviorSubject-based (not Signals) deliberately -- Signals adoption
 * for state services is a Phase 4 modernization concern, out of scope while
 * porting the app to Angular 22.
 */
@Injectable({ providedIn: 'root' })
export class SessionStateService {
    private readonly authStateVersionStorageKey = 'auth_state_version'

    private authStateVersion: number

    readonly userLoggedInSource = new BehaviorSubject(
        this.isAuthenticated()
    )

    readonly userLoggedIn$ = this.userLoggedInSource.asObservable()

    user: any

    // The session cookie is shared by every tab, so tabs must tell each other when it changes.
    private readonly channel =
        typeof BroadcastChannel !== 'undefined'
            ? new BroadcastChannel('rg4-auth')
            : null

    private readonly remoteSource = new Subject<AuthBroadcast>()

    /** Auth events coming from OTHER tabs (a channel never delivers to its own sender). */
    readonly remote$ = this.remoteSource.asObservable()

    constructor() {
        this.channel?.addEventListener('message', (e: MessageEvent) =>
            this.remoteSource.next(e.data)
        )

        const storedAuthStateVersion = Number(
            localStorage.getItem(this.authStateVersionStorageKey) ?? '0'
        )
        this.authStateVersion = Number.isNaN(storedAuthStateVersion)
            ? 0
            : storedAuthStateVersion

        if (localStorage.getItem('user')) {
            this.user = JSON.parse(localStorage.getItem('user') as string)
        }
    }

    isAuthenticated(): boolean {
        return !!localStorage.getItem('user')
    }

    /** A fresh sign-in (form, social, verification link): the only place a login is recorded. */
    setUser(user: any): void {
        this.bumpAuthStateVersion()
        this.updateUser(user)
    }

    /** Replace the current user (login, role switch, refreshed permissions); localStorage is only a cache of it. */
    updateUser(user: any): void {
        localStorage.setItem('user', JSON.stringify(user))
        this.user = user
        this.userLoggedInSource.next(true)
    }

    /**
     * `remote` = another tab already ended (or replaced) the session: only reset this
     * tab's own state. localStorage `user` and the shared cookies are left alone, since
     * they may already belong to a new login in that other tab.
     */
    logOut(opts: { remote?: boolean } = {}): void {
        this.bumpAuthStateVersion()
        if (!opts.remote) {
            localStorage.removeItem('user')
            sessionStorage.clear()
            this.clearBrowserCookies()
        }
        this.user = undefined
        this.userLoggedInSource.next(false)
        if (!opts.remote) {
            this.announce({ type: 'logout' })
        }
    }

    announce(msg: AuthBroadcast): void {
        try {
            this.channel?.postMessage(msg)
        } catch { /* channel closed: nothing to notify */ }
    }

    getAuthStateVersion(): number {
        return this.authStateVersion
    }

    /** Single owner of the counter: ApiService delegates here so the header and the interceptor agree. */
    bumpAuthStateVersion(): void {
        // max(): another tab may have bumped the shared stored value past ours
        const stored = Number(
            localStorage.getItem(this.authStateVersionStorageKey) ?? '0'
        )
        this.authStateVersion =
            Math.max(this.authStateVersion, Number.isNaN(stored) ? 0 : stored) + 1
        localStorage.setItem(
            this.authStateVersionStorageKey,
            this.authStateVersion.toString()
        )
    }

    // Clears every non-HttpOnly cookie JS can see, also on parent domains (app.example.com -> .example.com)
    private clearBrowserCookies(): void {
        const cookies = document.cookie ? document.cookie.split(';') : []
        const parts = window.location.hostname.split('.')
        const domains = [window.location.hostname]
        for (let i = 1; i < parts.length; i++) {
            domains.push(`.${parts.slice(i).join('.')}`)
        }

        for (const cookie of cookies) {
            const eqPos = cookie.indexOf('=')
            const name = (eqPos > -1 ? cookie.substring(0, eqPos) : cookie).trim()
            if (!name) {
                continue
            }

            document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/`
            domains.forEach((domain) => {
                document.cookie = `${name}=;expires=Thu, 01 Jan 1970 00:00:00 GMT;path=/;domain=${domain}`
            })
        }
    }
}
